/**
 * Task Health Badge Component
 * Displays visual health indicator for tasks with color coding
 */

import React from 'react';
import { Task } from '../types';
import { getTaskHealth } from '../services/notificationService';
import StatusBadge, { BadgeTone } from './StatusBadge';
import { AlertCircle, AlertTriangle, Clock, CheckCircle, Ban } from 'lucide-react';

interface TaskHealthBadgeProps {
  task: Task;
  showDetails?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export default function TaskHealthBadge({ task, showDetails = true, size = 'md' }: TaskHealthBadgeProps) {
  const health = getTaskHealth(task);

  const getTone = (): BadgeTone => {
    switch (health.status) {
      case 'overdue':
      case 'blocked':
        return 'destructive';
      case 'at-risk':
      case 'needs-update':
        return 'warning';
      case 'healthy':
      default:
        return 'success';
    }
  };

  const getIcon = () => {
    const iconSize = size === 'sm' ? 'w-3 h-3' : size === 'md' ? 'w-4 h-4' : 'w-5 h-5';
    
    switch (health.status) {
      case 'overdue':
        return <AlertCircle className={iconSize} />;
      case 'at-risk':
        return <AlertTriangle className={iconSize} />;
      case 'needs-update':
        return <Clock className={iconSize} />;
      case 'blocked':
        return <Ban className={iconSize} />;
      case 'healthy':
        return <CheckCircle className={iconSize} />;
    }
  };

  return (
    <StatusBadge
      tone={getTone()}
      icon={getIcon()}
      label={showDetails ? health.message : health.status.replace('-', ' ')}
      size={size}
      variant="outline"
    />
  );
}
